import { ReactNode, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import Sidebar from './Sidebar'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [currentPage, setCurrentPage] = useState('dashboard')

  useEffect(() => {
    const pathToPageMap: Record<string, string> = {
      '/': 'dashboard',
      '/home': 'dashboard',
      '/workspace': 'workspace',
      '/board': 'board',
      '/calendar': 'calendar',
      '/chat': 'chat',
      '/notes': 'notes',
      '/teams': 'teams',
      '/projects': 'projects',
      '/contacts': 'contacts',
    }
    
    const currentPath = location.pathname
    
    // Handle dynamic routes
    let page = 'dashboard'
    if (currentPath.startsWith('/workspace')) {
      page = 'workspace'
    } else if (currentPath.startsWith('/dashboard')) {
      page = 'dashboard'
    } else if (currentPath.startsWith('/board')) {
      page = 'board'
    } else if (currentPath.startsWith('/calendar')) {
      page = 'calendar'
    } else if (currentPath.startsWith('/chat')) {
      page = 'chat'
    } else if (currentPath.startsWith('/notes')) {
      page = 'notes'
    } else if (currentPath.startsWith('/teams')) {
      page = 'teams'
    } else if (currentPath.startsWith('/projects')) {
      page = 'projects'
    } else if (currentPath.startsWith('/contacts')) {
      page = 'contacts'
    } else {
      page = pathToPageMap[currentPath] || 'dashboard'
    }
    
    setCurrentPage(page)
  }, [location.pathname])

  const handlePageChange = (page: string) => {
    setCurrentPage(page)
    // Navigation is now handled in the Sidebar component directly
    // This function is kept for state management only
  }

  return (
    <div className="workspace-layout flex h-screen relative overflow-hidden">
      {/* ChatGPT-style radial gradient background - exact implementation */}
      <div className="absolute inset-0 -z-10">
        {/* Base white/light background with transparency */}
        <div className="absolute inset-0 bg-white/80" />
        
        {/* Multiple overlapping radial gradients distributed across viewport */}
        <div 
          className="absolute inset-0 opacity-35"
          style={{
            background: `
              radial-gradient(at 20% 30%, rgb(251, 146, 170) 0px, transparent 50%),
              radial-gradient(at 80% 20%, rgb(255, 248, 220) 0px, transparent 50%),
              radial-gradient(at 50% 50%, rgb(254, 202, 138) 0px, transparent 50%),
              radial-gradient(at 30% 80%, rgb(147, 197, 253) 0px, transparent 50%),
              radial-gradient(at 70% 70%, rgb(251, 146, 170) 0px, transparent 50%),
              radial-gradient(at 40% 40%, rgb(255, 245, 200) 0px, transparent 50%),
              radial-gradient(at 60% 30%, rgb(255, 225, 180) 0px, transparent 50%),
              radial-gradient(at 85% 15%, rgb(255, 235, 180) 0px, transparent 50%),
              radial-gradient(at 75% 35%, rgb(255, 240, 200) 0px, transparent 50%)
            `
          }}
        />
        
        {/* Overlay for depth and softness - ChatGPT-inspired gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/40" />
      </div>
      
      <Sidebar currentPage={currentPage} onPageChange={handlePageChange} />
      <main className="flex-1 overflow-x-visible overflow-y-auto relative bg-transparent">
        {children}
      </main>
    </div>
  )
}